import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { closeGameRules, closeMenu } from "../../../../State/actions";
import { selectGameRulesActive, selectIosFullscreenOverlayActive } from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import { useToolbarDimensionsV2 } from "../../../../Hooks/useToolbarDimensionsV2";
import { getRulesPath, getAssetsPath } from "../../../../utils/pathConfig";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

const MAX_WIN_MULTIPLIER = 5000;

const GameRulesOverlay = () => {
  const dispatch = useDispatch();
  const gameRulesActive = useSelector(selectGameRulesActive);
  const iosFullscreenOverlayActive = useSelector(selectIosFullscreenOverlayActive);
  const { i18n, t } = useTranslation();
  const { dimensions, device, orientation, uiSymbolScale } = useDimensions();
  const toolbar = useToolbarDimensionsV2();

  // Get RTP value from window.__OPTIONS__
  const rtpValue = window.__OPTIONS__?.math?.rtp?.main || "";

  const [htmlContent, setHtmlContent] = useState("");
  const overlayRef = useRef(null);

  // Calculate viewport configuration (same logic as PixiJS GameRules)
  const viewportConfig = useMemo(() => {
    const getViewportConfig = () => {
      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.8,
            paddingTop: dimensions.height / 5.5,
            paddingBottom: dimensions.height / 10,
            titleFontSizeMultiplier: 1.35,
            closeButtonOffsetMultiplier: 1.65,
            logoSizeMultiplier: 1.5,
          };
        } else {
          return {
            maxWidth: toolbar.width * 0.575,
            paddingTop: dimensions.height / 3.0,
            paddingBottom: dimensions.height / 8.5,
            titleFontSizeMultiplier: 1.1,
            closeButtonOffsetMultiplier: 1.35,
            logoSizeMultiplier: 1.5,
          };
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.86,
            paddingTop: dimensions.height / 4.6,
            paddingBottom: dimensions.height / 9.5,
            titleFontSizeMultiplier: 1.45,
            closeButtonOffsetMultiplier: 1.75,
            logoSizeMultiplier: 1.75,
          };
        } else {
          return {
            maxWidth: toolbar.width * 0.8,
            paddingTop: dimensions.height / 4.5,
            paddingBottom: dimensions.height / 9.5,
            titleFontSizeMultiplier: 1.0,
            closeButtonOffsetMultiplier: 1.65,
            logoSizeMultiplier: 1.5,
          };
        }
      } else {
        return {
          maxWidth: toolbar.width * 0.575,
          paddingTop: dimensions.height / 4,
          paddingBottom: dimensions.height / 8,
          titleFontSizeMultiplier: 1.0,
          closeButtonOffsetMultiplier: 1.85,
          logoSizeMultiplier: 1.0,
        };
      }
    };

    const config = getViewportConfig();
    const viewportHeight =
      dimensions.height - config.paddingTop - config.paddingBottom;

    return {
      ...config,
      viewportHeight,
    };
  }, [dimensions, device, orientation, toolbar.width]);

  // Calculate font sizes (matching PixiJS logic)
  const baseTitleFontSize = 56;
  const baseParagraphFontSize = 28;
  const baseSectionTitleFontSize = 32;

  const fontSizes = useMemo(() => {
    let titleMultiplier = 1.0;
    let paragraphMultiplier = 1.0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 2.0;
        paragraphMultiplier = 2.0;
      } else {
        titleMultiplier = 1.15;
        paragraphMultiplier = 1.15;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 2.2;
        paragraphMultiplier = 2.2;
      } else {
        titleMultiplier = 1.6;
        paragraphMultiplier = 1.5;
      }
    }

    return {
      title: Math.floor(
        baseTitleFontSize * viewportConfig.titleFontSizeMultiplier * uiSymbolScale
      ),
      sectionTitle: Math.floor(
        baseSectionTitleFontSize * titleMultiplier * uiSymbolScale
      ),
      paragraph: Math.floor(
        baseParagraphFontSize * paragraphMultiplier * uiSymbolScale
      ),
    };
  }, [
    device,
    orientation,
    uiSymbolScale,
    viewportConfig.titleFontSizeMultiplier,
  ]);

  // Load HTML content based on current locale
  useEffect(() => {
    const loadRulesHTML = async () => {
      try {
        const locale = i18n.language || "en";
        const fallbackLng = "en";

        // Use centralized path configuration
        const htmlPath = getRulesPath(locale, fallbackLng);

        let response = await fetch(htmlPath);

        // If locale-specific file not found, fall back to default rules.html
        if (!response.ok) {
          response = await fetch(getRulesPath(fallbackLng, fallbackLng));
        }

        if (!response.ok) {
          return;
        }

        let html = await response.text();

        // Replace placeholders with actual values
        html = html.replace(
          /{MAX_MULTIPLIER}/g,
          MAX_WIN_MULTIPLIER.toLocaleString()
        );
        html = html.replace(/{RTP_VALUE}/g, rtpValue.toString());

        setHtmlContent(html);
      } catch (error) {
        // Fail silently
      }
    };

    if (gameRulesActive) {
      loadRulesHTML();
    }
  }, [gameRulesActive, i18n.language, rtpValue]);

  // Handle close
  const handleClose = useCallback(() => {
    // Play click sound
    soundManager.play(SOUND_NAME.button_click);
    
    dispatch(closeGameRules());
    dispatch(closeMenu());
  }, [dispatch]);

  // Calculate logo size based on device configuration
  const logoSize = useMemo(() => {
    const baseWidth = 300;
    const baseHeight = 150;

    return {
      width: baseWidth * uiSymbolScale * viewportConfig.logoSizeMultiplier,
      height: baseHeight * uiSymbolScale * viewportConfig.logoSizeMultiplier,
    };
  }, [uiSymbolScale, viewportConfig.logoSizeMultiplier]);

  // Memoize translation text
  const rulesTitleText = useMemo(
    () => t("rules_title", { ns: "common" }),
    [t]
  );

  // Memoize position calculations
  const logoTop = useMemo(
    () => viewportConfig.paddingTop - fontSizes.title * 1.8 - logoSize.height,
    [viewportConfig.paddingTop, fontSizes.title, logoSize.height]
  );

  const separatorLineTop = useMemo(
    () => viewportConfig.paddingTop - fontSizes.title * 2.0,
    [viewportConfig.paddingTop, fontSizes.title]
  );

  const titleTop = useMemo(
    () => viewportConfig.paddingTop - fontSizes.title * 1.5,
    [viewportConfig.paddingTop, fontSizes.title]
  );

  const closeButtonTop = useMemo(
    () => logoTop,
    [logoTop]
  );

  const closeButtonRight = useMemo(
    () => (dimensions.width - viewportConfig.maxWidth) / 2,
    [dimensions.width, viewportConfig.maxWidth]
  );

  const closeButtonSize = useMemo(
    () => fontSizes.title * 1.2,
    [fontSizes.title]
  );

  const closeButtonTransformY = useMemo(
    () => logoSize.height / 2 - closeButtonSize / 2,
    [logoSize.height, closeButtonSize]
  );

  const separatorLineWidth = useMemo(
    () => viewportConfig.maxWidth * 1.1,
    [viewportConfig.maxWidth]
  );

  // Prevent body scroll when overlay is active
  useEffect(() => {
    if (gameRulesActive) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [gameRulesActive]);

  if (!gameRulesActive) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      className="game-rules-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        animation: "fadeIn 0.2s ease-out",
        pointerEvents: "auto",
        opacity: iosFullscreenOverlayActive ? 0 : 1,
        visibility: iosFullscreenOverlayActive ? "hidden" : "visible",
        transition: "opacity 0.2s ease, visibility 0.2s ease",
      }}
    >
      {/* Game Logo */}
      <img
        src={`${getAssetsPath()}/images/logo_portrait.webp`}
        alt="Game Logo"
        style={{
          position: "absolute",
          top: `${logoTop}px`,
          left: "50%",
          transform: "translateX(-50%)",
          width: `${logoSize.width}px`,
          height: `${logoSize.height}px`,
          objectFit: "contain",
          zIndex: 1002,
        }}
      />

      {/* Title separator line */}
      <div
        style={{
          position: "absolute",
          top: `${separatorLineTop}px`,
          left: "50%",
          transform: "translateX(-50%)",
          width: `${separatorLineWidth}px`,
          height: "3px",
          backgroundColor: "#feff00",
        }}
      />

      {/* Title */}
      <h1
        style={{
          position: "absolute",
          top: `${titleTop}px`,
          left: "50%",
          transform: "translateX(-50%)",
          fontFamily: "Noto Sans, sans-serif",
          fontSize: `${fontSizes.title}px`,
          fontWeight: "bold",
          color: "#feff00",
          margin: 0,
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        {rulesTitleText}
      </h1>

      {/* Close button */}
      <button
        onClick={handleClose}
        style={{
          position: "absolute",
          top: `${closeButtonTop}px`,
          right: `${closeButtonRight}px`,
          width: `${closeButtonSize}px`,
          height: `${closeButtonSize}px`,
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
          zIndex: 1001,
          transition: "all 0.2s ease",
          backgroundImage: `url('${getAssetsPath()}/user-interface-secondary/icon_close.webp')`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          transform: `translateY(${closeButtonTransformY}px)`,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = `translateY(${closeButtonTransformY}px) scale(1.1)`;
          e.currentTarget.style.filter = "brightness(2.0)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = `translateY(${closeButtonTransformY}px) scale(1)`;
          e.currentTarget.style.filter = "brightness(1)";
        }}
      />


      {/* Scrollable content container */}
      <div
        className="game-rules-content"
        style={{
          position: "absolute",
          top: `${viewportConfig.paddingTop}px`,
          left: "50%",
          transform: "translateX(-50%)",
          width: `${viewportConfig.maxWidth}px`,
          height: `${viewportConfig.viewportHeight}px`,
          overflowY: "auto",
          overflowX: "hidden",
          fontFamily: "Noto Sans, sans-serif",
          color: "#ffffff",
          padding: `${fontSizes.paragraph * 0.8}px 0`,
          scrollbarWidth: "thin",
          scrollbarColor: "#feff00 rgba(255, 255, 255, 0.1)",
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {/* Inject dynamic styles for content */}
      <style>{`
        .game-rules-overlay {
          font-family: 'Noto Sans', sans-serif;
        }

        .game-rules-content h3 {
          font-size: ${fontSizes.sectionTitle}px;
          font-weight: bold;
          color: #feff00;
          margin: ${fontSizes.paragraph * 1.2}px 0 ${
        fontSizes.paragraph * 0.9
      }px 0;
          line-height: ${fontSizes.sectionTitle * 1.2}px;
        }

        .game-rules-content p {
          font-size: ${fontSizes.paragraph}px;
          font-weight: 400;
          color: #ffffff;
          margin: 0 0 ${fontSizes.paragraph * 0.9}px 0;
          line-height: ${fontSizes.paragraph * 1.3}px;
        }

        .game-rules-content ul {
          margin: 0 0 ${fontSizes.paragraph * 0.9}px 0;
          padding-left: ${fontSizes.paragraph * 1.5}px;
        }

        .game-rules-content li {
          font-size: ${fontSizes.paragraph}px;
          font-weight: 400;
          color: #ffffff;
          margin-bottom: ${fontSizes.paragraph * 0.35}px;
          line-height: ${fontSizes.paragraph * 1.3}px;
        }

        .game-rules-content section {
          margin-bottom: ${fontSizes.paragraph * 0.65}px;
        }

        .game-rules-content strong {
          font-weight: 700;
        }

        /* Custom scrollbar for webkit browsers */
        .game-rules-content::-webkit-scrollbar {
          width: 8px;
        }

        .game-rules-content::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }

        .game-rules-content::-webkit-scrollbar-thumb {
          background: #feff00;
          border-radius: 4px;
        }

        .game-rules-content::-webkit-scrollbar-thumb:hover {
          background: #ffff33;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default GameRulesOverlay;
